import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import PopoverTooltip from "../../components/interface/PopoverTooltip";
import { withScreenContext } from "../../components/screen/ScreenContext";
import {
  RELATIONSHIP_ENTITY_COLUMN_WIDTH,
  RELATIONSHIP_EVENT_COLUMN_WIDTH,
  RELATIONSHIP_HEADER_HEIGHT
} from "../../constants";
import { GLOBAL_FILTER_ID } from "../../constants/identifiers";
import { ADD_COLUMN_PREFIX } from "../../constants/idPrefixes";
import interfaceMessages from "../../constants/interfaceMessages";
import interfaceUserActions from "../../constants/interfaceUserActions";
import PubSubTopics from "../../constants/pubSubTopics";
import sortOptions from "../../constants/sortOptions";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import tableDropAction from "../../constants/tableDropAction";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { batchActions } from "../../modules/chainReducer";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { stringFromDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import dropSelectors from "../../modules/dropSelectors";
import { filterSelectors } from "../../modules/filters";
import { itemOperations, itemSelectors } from "../../modules/items";
import { narrativeSelectors } from "../../modules/narrative";
import { relationshipOperations } from "../../modules/relationships";
import { sequenceOperations, sequenceSelectors } from "../../modules/sequence";
import { timelineReducer } from "../../modules/timelineReducer";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { shouldAutoEditItems } from "../../modules/undoRedo";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { onPasteItems } from "../../utils/copyPaste";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { trackEvent } from "../../utils/trackAnalytics";
import { appSelectors } from "../app";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import RelationshipCanvasView from "./RelationshipCanvasView";
import selectors from "./selectors";

// import debugUtils from "../../utils/debugUtils";

class RelationshipView extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      data: immutableRecords.RelationshipViewDisplayRecord({}),
      draggedItemIds: List()
    };

    this.dispatchLocal = this.dispatchLocal.bind(this);
    this.startRowDrag = this.startRowDrag.bind(this);
    this.onMoveRow = this.onMoveRow.bind(this);
    this.dispatchLastMoveAction = this.dispatchLastMoveAction.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.onCanItemDropOnRow = this.onCanItemDropOnRow.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.onToggleChildren = this.onToggleChildren.bind(this);
    this.onViewClick = this.onViewClick.bind(this);
    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);
    this.canTypeHaveSuperType = this.canTypeHaveSuperType.bind(this);

    // copy paste
    this._pubSubPasteToken = null;
    this.onPasteMessageReceived = this.onPasteMessageReceived.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.processPastingItems = this.processPastingItems.bind(this);
  }

  componentWillMount() {
    this.copyLocalStore(this.props);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this._pubSubPasteToken = PubSub.subscribe(PubSubTopics.paste, this.onPasteMessageReceived);
    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("RelationshipView", this.props, nextProps, true);
    this.copyLocalStore(nextProps);
  }

  componentWillUnmount() {
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
    PubSub.unsubscribe(this._pubSubPasteToken);
  }

  canCreateItemOfType(typeId) {
    return typeSelectors.getTypeIsAllowedInSequence(this.props.localStore, typeId);
  }

  getLastSelectedIndex() {
    return this.state.data.rows.findLastIndex(r => this.props.selectedItemIds.contains(r.itemDisplay.id));
  }

  onCreateItem(typeId, positionLast) {
    // If this type is not allowed in sequence, then don't add it here.
    if (!this.canCreateItemOfType(typeId)) {
      return false;
    }
    // Otherwise, we can add it.
    // Do we want to add it after current selection?
    const lastSelectedIndex = this.getLastSelectedIndex();

    if (lastSelectedIndex >= 0 && !positionLast) {
      const lastRow = this.state.data.rows.get(lastSelectedIndex);
      this.props.onCreateItem(
        typeId,
        lastRow.itemDisplay.super,
        lastRow.itemDisplay.sequenceIndex + 1,
        this.props.viewId
      );
    } else {
      this.props.onCreateItem(typeId, null, null, this.props.viewId);
    }
    return true;
  }

  processPastingItems(itemMapList) {
    let extraActions = [];
    const rowsCount = this.state.data.rows.size;
    const lastSelectedIndex = this.getLastSelectedIndex();
    const lastRow =
      rowsCount > 0 ? this.state.data.rows.get(lastSelectedIndex >= 0 ? lastSelectedIndex : rowsCount - 1) : null;
    if (lastRow) {
      const lastItemDisplay = lastRow.itemDisplay;
      const superId = lastItemDisplay.super || null;
      let sequenceIndex = lastItemDisplay.sequenceIndex;
      itemMapList.forEach(im => {
        // Set super if has item selected
        if (superId) {
          extraActions.push(itemOperations.setSuper([im.get("id")], superId));
        }
        // Set sequence index after the selected item or the last item
        sequenceIndex += 1;
        extraActions.push(sequenceOperations.moveItemsInSequence([im.get("id")], sequenceIndex));
      });
    }
    return extraActions;
  }

  onPaste(e) {
    const {
      dispatchBatchActions,
      projectId,
      isActiveView,
      types,
      defaultType,
      allTypesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    } = this.props;
    dispatchBatchActions(
      onPasteItems(
        e,
        projectId,
        isActiveView,
        itemMap => this.canCreateItemOfType(itemMap.get("type")),
        this.processPastingItems,
        types.map(t => t.id),
        defaultType,
        typeSelectors.isDatedType,
        allTypesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      )
    );
  }

  onPasteMessageReceived(msg, clipboardData) {
    this.onPaste({ clipboardData });
  }

  copyLocalStore(props) {
    this.setState({
      data: selectors.getRelationshipViewData(props.localStore, props.viewId),
      draggedItemIds: List()
    });
  }

  dispatchLocal(action, draggedIds) {
    // Dispatch local needs to apply to the original state each time, so we always apply it to props.localStore instead
    const newStore = timelineReducer(this.props.localStore, action, this.props.localStore);
    const data = selectors.getRelationshipViewData(newStore, this.props.viewId);
    this.setState({
      data,
      draggedItemIds: List(draggedIds)
    });
  }

  internalDragLeave() {
    this.copyLocalStore(this.props);
  }

  startRowDrag(itemIds) {
    this.setState({
      draggedItemIds: List(itemIds)
    });
  }

  requestCanDropItems(
    itemIds,
    dropRows,
    dropDate,
    dropAction,
    dropRow,
    newDropRowIndex,
    adjustItemEndWhenChangingStart
  ) {
    const { sortBy } = this.props;
    if (sortBy === sortOptions.SEQUENCE) {
      if (!itemSelectors.getItemsAreAllowedInSequence(this.props.localStore, itemIds)) {
        return {
          canDrop: false,
          message: interfaceMessages.DRAG_DROP_DATE_ORDER_NOT_ALLOWED
        };
      }
    } else {
      if (!itemSelectors.getItemsAreAllowedInNarrative(this.props.localStore, itemIds)) {
        return {
          canDrop: false,
          message: interfaceMessages.DRAG_DROP_NARRATIVE_ORDER_NOT_ALLOWED
        };
      }
    }

    let newDropDateString;
    const newSequenceIndex = sequenceSelectors.getItemSequenceIndex(this.props.localStore, dropRow.itemDisplay.id);
    const dropSequenceIndex = dropAction === tableDropAction.DropBelowRow ? newSequenceIndex + 1 : newSequenceIndex;
    const rows = dropRows.filter(row => itemIds.includes(row.itemDisplay.id));
    for (let i = 0; i < rows.size; i++) {
      const item = rows.get(i).itemDisplay;

      // If the drag will change the item's date, show it in the drag message
      if (isDateValid(dropDate)) {
        if (isDateValid(item.startDate) && item.startDate.timestamp !== dropDate.timestamp) {
          const sequenceIndex = sequenceSelectors.getItemSequenceIndex(this.props.localStore, item.id);
          if (sequenceIndex !== dropSequenceIndex) {
            const superId = dropRow.itemDisplay.super;

            const newDropDate = dropSelectors.getDropDateChange(
              this.props.itemSequence,
              dropSequenceIndex,
              item.id,
              item,
              itemIds,
              this.props.itemsById,
              superId
            );

            if (newDropDate && newDropDate.timestamp != item.startDate.timestamp) {
              newDropDateString = `Move to: ${stringFromDate(this.props.calendar, newDropDate)}`;

              // Block the drop if any dates are locked
              if (item.isDateLocked) {
                return {
                  canDrop: false,
                  message: interfaceMessages.DRAG_DROP_LOCKED_DATES
                };
              }
            }
          }
        }
      }
    }

    // If !adjustItemEndWhenChangingStart, only allow the drop if it isn't later than dates other than start date
    if (!adjustItemEndWhenChangingStart && dropDate) {
      const rows = dropRows.filter(row => itemIds.includes(row.itemDisplay.id));
      for (let i = 0; i < rows.size; i++) {
        const item = rows.get(i);
        if (item) {
          if (
            isDateValid(item.itemDisplay.endDate) &&
            item.itemDisplay.endDate.timestamp != item.itemDisplay.startDate.timestamp &&
            dropDate.timestamp > item.itemDisplay.endDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_END_DATE_NOT_ALLOWED
            };
          }

          if (
            isDateValid(item.itemDisplay.latestStartDate) &&
            item.itemDisplay.latestStartDate.timestamp != item.itemDisplay.startDate.timestamp &&
            dropDate.timestamp > item.itemDisplay.latestStartDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_LATEST_START_DATE_NOT_ALLOWED
            };
          }

          if (
            isDateValid(item.itemDisplay.earliestEndDate) &&
            item.itemDisplay.earliestEndDate.timestamp != item.itemDisplay.startDate.timestamp &&
            dropDate.timestamp > item.itemDisplay.earliestEndDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_EARLIEST_END_DATE_NOT_ALLOWED
            };
          }
        }
      }
    }
    return { canDrop: true, message: newDropDateString ? newDropDateString : null };
  }

  dispatchLastMoveAction() {
    if (this._lastAction) {
      this.props.onDispatchAction(this._lastAction);
    }
    this.setState({
      draggedItemIds: List()
    });
    this._lastAction = null;
  }

  canDrop(_props, _dragItem, _dropTarget) {
    const { sortBy } = this.props;
    return sortBy === sortOptions.SEQUENCE || sortBy === sortOptions.NARRATIVE;
  }

  onMoveRow(dragItem, dropRow, dropAction) {
    const { localStore, sortBy } = this.props;
    const dragItemIds = dragItem.multipleItemIds;
    const dropTargetId = dropRow ? dropRow.itemDisplay.id : null;
    let action = dropSelectors.getActionForTableDrop(localStore, dragItemIds, dropTargetId, dropAction, sortBy);
    if (action) {
      const fromExternalSource = this.props.viewId !== dragItem.sourceView;

      // If the drag source was outside this view, then we want to auto-apply filters
      if (fromExternalSource) {
        action = batchOperations.matchFiltersAfterDropAction(action, dragItemIds, this.props.viewId);
      }

      trackEvent("setItemOrderOrGroupByDragFromRelationshipView", "setItemOrderOrGroupByDrag");
      this.props.onDispatchAction(action, this.props.itemsById);
      //#!if iOS !== true
      this.props.onSelectItems(dragItemIds);
      //#!endif
    }
  }

  onCanItemDropOnRow(dragItem, dropRow) {
    if (this.props.sortBy !== sortOptions.SEQUENCE) {
      return true;
    }
    // Ensure that the two item types are allowed a super-child relationship
    // We do not check for a super loop here, as the table view takes care of it now itself.
    const childType = dragItem.itemType;
    const superType = dropRow && dropRow.id ? this.props.itemsById.get(dropRow.id).type : null;
    return superType ? typeSelectors.canTypeHaveSuperType(this.props.allTypesById, childType, superType) : false;
  }

  onToggleChildren(id, isExpanded) {
    this.props.onToggleChildren(this.props.viewId, id, isExpanded);
  }

  onViewClick(e) {
    const clickWasItem =
      hasAncestorOfClass(e.target, "relationshipTableRow") || hasAncestorOfClass(e.target, "rEntityCol");
    if (!clickWasItem && this.props.selectedItemIds.size > 0) {
      this.props.onViewClick();
    }
  }

  requestViewBoundsForZoom(_zoom) {
    return immutableRecords.FlatRectRecord({
      left: 0,
      width: this.state.data.width,
      right: this.state.data.width,
      top: 0,
      height: this.state.data.height,
      bottom: this.state.data.height
    });
  }

  canTypeHaveSuperType(childType, superType) {
    return typeSelectors.canTypeHaveSuperType(this.props.allTypesById, childType, superType);
  }

  render() {
    const {
      fontSize,
      viewId,
      isActiveView,
      sortedItemIds,
      selectedItemIds,
      allowedReferences,
      allowedInverseReferences,
      relationshipViewSettings,
      onSelectItems,
      onAddToSelection,
      onRemoveFromSelection,
      onLabelChange,
      onRelationshipChange,
      itemsById
    } = this.props;

    // const displayData = this.state.data.update("rows", rows =>
    //   rows.filter(r => !this.state.draggedItemIds.contains(r.id))
    // );

    const displayData = this.state.data;

    const hasData = selectors.doesRelationshipViewHaveData(this.props.localStore, this.props.viewId);

    let allowRowDrag = true;
    //#!if iOS === true
// allowRowDrag = this.props.screenContext.isEditMode;
    //#!endif

    const defaultTypeLabel = this.props.types.find(type => type.id === this.props.defaultType).label;

    const noEvents = !displayData.rows.size;
    const noCols = !displayData.colGroups.find(c => c.cols.size > 1);
    let noRelationships = false;
    if (!noEvents && !noCols) {
      noRelationships = !displayData.rows.find(
        e => e.itemDisplay.references.size || e.itemDisplay.inverseReferences.size
      );
    }

    let showPopover = noRelationships;
    //#!if iOS === true
// showPopover = noRelationships && selectedItemIds.size === 0;
    //#!endif

    let tooltip = null;
    if (showPopover) {
      const elementId = "relationshipEmptyStateTooltip";
      const cols = displayData.colGroups.map(cg => cg.cols).flatten();
      const firstCol = cols.find(c => c.id.indexOf(ADD_COLUMN_PREFIX) === -1);
      const SCALED_COLUMN_WIDTH = scalePixelsToFontSize(RELATIONSHIP_EVENT_COLUMN_WIDTH, this.props.fontSize);
      const tooltipLeft = SCALED_COLUMN_WIDTH + RELATIONSHIP_ENTITY_COLUMN_WIDTH / 2 + firstCol.left + "px";
      const tooltipTop = scalePixelsToFontSize(RELATIONSHIP_HEADER_HEIGHT + 20, this.props.fontSize);
      let message = "Click an intersection to create relationships";
      //#!if iOS === true
// message = "Tap an intersection to create relationships";
      //#!endif
      tooltip = (
        <Fragment>
          <div id={elementId} style={{ position: "absolute", left: tooltipLeft, top: tooltipTop }} />
          <PopoverTooltip
            target={elementId}
            tooltipOpen={true}
            key="relationshipIntersectionTooltip"
            label={message}
            popoverPlacement="right"
          />
        </Fragment>
      );
    }

    const result = (
      <Fragment>
        <RelationshipCanvasView
          fontSize={fontSize}
          data={displayData}
          hasData={hasData}
          viewId={viewId}
          isActiveView={isActiveView}
          sortedItemIds={sortedItemIds}
          selectedItemIds={selectedItemIds}
          itemsById={itemsById}
          allowedReferences={allowedReferences}
          allowedInverseReferences={allowedInverseReferences}
          relationshipViewSettings={relationshipViewSettings}
          onPaste={this.onPaste}
          onSelectItems={onSelectItems}
          onAddToSelection={onAddToSelection}
          onRemoveFromSelection={onRemoveFromSelection}
          onLabelChange={onLabelChange}
          onToggleChildren={this.onToggleChildren}
          onRelationshipChange={onRelationshipChange}
          onViewClick={this.onViewClick}
          onLiveColumnDrag={this.internalMoveColumn}
          onColumnDrop={this.dispatchLastMoveAction}
          allowRowDrag={allowRowDrag}
          allowRowDragNesting={true}
          draggedRowIds={this.state.draggedItemIds}
          onStartRowDrag={this.startRowDrag}
          onRowDrop={this.onMoveRow}
          canDrop={this.canDrop()}
          requestViewBoundsForZoom={this.requestViewBoundsForZoom}
          canItemDropOnRow={this.onCanItemDropOnRow}
          canTypeHaveSuperType={this.canTypeHaveSuperType}
          autoEditBlankNodes={this.props.autoEditBlankNodes}
          requestCanDropItems={this.requestCanDropItems}
          adjustItemEndWhenChangingStart={this.props.adjustItemEndWhenChangingStart}
          onCreateItem={this.onCreateItem}
          types={this.props.types}
          defaultType={this.props.defaultType}
          typesById={this.props.typesById}
          defaultTypeLabel={defaultTypeLabel}
          isGlobalFilterActive={this.props.isGlobalFilterActive}
          isViewFilterActive={this.props.isViewFilterActive}
          onShowInspector={this.props.onShowInspector}
        />
        {tooltip}
      </Fragment>
    );
    return result;
  }
}

const mapStateToProps = (state, props) => {
  return {
    localStore: state.present,
    calendar: calendarSelectors.getCalendar(state.present),
    // data: selectors.getRelationshipViewData(state.present, props.viewId),
    sortedItemIds: selectors.getRelationshipViewSortedItemIds(state.present, props.viewId),
    itemsById: itemSelectors.getItemsById(state.present),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    allowedReferences: selectors.getAllowedReferencesPerSubjectObjectTypes(state.present),
    allowedInverseReferences: selectors.getAllowedInverseReferencesPerSubjectObjectTypes(state.present),
    relationshipViewSettings: definitionSelectors.getRelationshipViewSettings(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    sortBy: appStateSelectors.getRelationshipViewSortBy(state.present, props.viewId),
    narrativeTree: narrativeSelectors.getNarrativeTree(state.present),
    allTypesById: definitionSelectors.getTypesById(state.present),
    projectId: appSelectors.getProjectId(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    autoEditBlankNodes: shouldAutoEditItems(state),
    adjustItemEndWhenChangingStart: definitionSelectors.getAdjustItemEndWhenChangingStart(state.present),
    types: selectors.getTypes(state.present),
    defaultType: selectors.getRelationshipViewDefaultType(state.present, props.viewId),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    itemSequence: sequenceSelectors.getItemSequence(state.present),
    isGlobalFilterActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID),
    isViewFilterActive: filterSelectors.isFilterWithIdActive(state.present, props.viewId)
  };
};

const readonlyActions = {
  onSelectItems: appStateOperations.selectItems,
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onToggleChildren: appStateOperations.toggleExpandSuperItem,
  onViewClick: appStateOperations.clearSelection,
  onShowInspector: uiStateOperations.showInspectorModal
};

const editingActions = {
  onRelationshipChange: (subjectId, referenceId, itemId, isChecked) => {
    trackEvent("setItemRelationshipFromRelationshipView", "setItemRelationship");
    if (referenceId === "none") {
      return relationshipOperations.deleteRelationshipsWithValues(subjectId, null, itemId, true, true);
    } else if (isChecked) {
      const actions = [relationshipOperations.createRelationship([subjectId], referenceId, itemId)];
      actions.push(
        appStateOperations.userInterfaceActionPerformed(interfaceUserActions.CREATE_RELATIONSHIP_IN_RELATIONSHIP)
      );
      return batchActions(actions);
    } else {
      return relationshipOperations.deleteRelationshipsWithValues(subjectId, referenceId, itemId);
    }
  },
  onCreateItem: (typeId, superId, sequenceIndex, viewId) => {
    trackEvent("createItemFromRelationshipView", "createItem");
    if (_.isNil(sequenceIndex)) {
      return batchOperations.matchFiltersAfterCreateAction(batchOperations.createAndSelectItem(typeId), viewId);
    } else {
      return batchOperations.matchFiltersAfterCreateAction(
        batchOperations.createItemInSequenceWithSuper(typeId, superId, sequenceIndex),
        viewId
      );
    }
  },
  onLabelChange: itemOperations.editLabel,
  onDispatchAction: a => {
    return a;
  },
  dispatchBatchActions: actions => {
    return batchOperations.reBatchActions(actions);
  },
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

RelationshipView.propTypes = {
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  localStore: PropTypes.object.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  allowedReferences: PropTypeShapes.allowedReferencePerSubjectObjectMap.isRequired,
  allowedInverseReferences: PropTypeShapes.allowedReferencePerSubjectObjectMap.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  onToggleChildren: PropTypes.func.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onRelationshipChange: PropTypes.func.isRequired,
  fontSize: PropTypes.number.isRequired,
  relationshipViewSettings: PropTypeShapes.relationshipViewSettings.isRequired,
  onDispatchAction: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  narrativeTree: PropTypes.object.isRequired,
  allTypesById: ImmutablePropTypes.map.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,
  types: ImmutablePropTypes.list.isRequired,
  defaultType: PropTypes.string.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  itemSequence: ImmutablePropTypes.list.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  isGlobalFilterActive: PropTypes.bool,
  isViewFilterActive: PropTypes.bool,

  // copy paste
  dispatchBatchActions: PropTypes.func,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,

  onShowInspector: PropTypes.func,

  // Workspace context
  workViewRegistration: PropTypes.object.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const normalView = withWorkspaceContext(connect(mapStateToProps, mapDispatchToProps)(RelationshipView));
let exportedView = normalView;

//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
